import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Layout, Popover, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { managementOp } from 'store/ducks/Management';
import { RootState } from 'store/configureStore';
import { TitleListEN, TitleLists } from 'store/ducks/globalTypes';
import {
  IDataManagementLayoutProps,
  TitleKey
} from 'store/ducks/Management/types';
import { OptionType } from 'store/ducks/Global/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import CustomSelect from 'components/CustomSelect';
import Helpers from 'utils/Helpers';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import { globalOp } from 'store/ducks/Global';
import * as Styled from 'components/Layouts/Layout.styled';

declare global {
  interface Window {
    hasUnsavedChanges?: boolean;
    showUnsavedChangesModal?: () => Promise<boolean>;
  }
}

const { Sider } = Layout;
const { useToken } = theme;
const { Content } = Layout;

const DataManagementLayout: React.FC<IDataManagementLayoutProps> = ({
  activeTab,
  activeKey,
  children
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { token } = useToken();
  const { t } = useTranslation();
  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );

  const { lang, saveDisabled, drawTable, scheme, orgContext } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      drawTable: state.management.drawTable,
      saveDisabled: state.management.saveDisabled,
      scheme: state.global.scheme,
      orgContext: state.global.hierarchicalLevelsScope
    })
  );
  const industry = orgContext?.industryType || [];
  const page = location.pathname.split('/').pop();

  const titleKey: TitleKey = (() => {
    switch (page) {
      case 'auditFindings':
        return 'AUDIT_FINDINGS';
      case 'category':
        return 'CATEGORY';
      case 'product':
        return 'PRODUCT';
      case 'solutionType':
        return 'SOLUTION_TYPE';
      case 'taskType':
        return 'TASK_TYPE';
      default:
        return 'AUDIT_FINDINGS';
    }
  })();

  const safeModuleArray = Array.isArray(orgContext.module)
    ? orgContext.module
    : orgContext.module !== undefined && orgContext.module !== null
    ? [orgContext.module]
    : [];

  const selectedModuleLabels = safeModuleArray
    .map((m: any) => {
      if (typeof m === 'object' && m !== null) {
        return m.label;
      }

      const option = moduleOptionsState.find((o) => o.value === m);
      return option?.label ?? null;
    })
    .filter(Boolean);

  const titles = TitleLists[lang] || TitleListEN;

  const applyFilters = async () => {
    const baseParams = Helpers.toApiFilters(orgContext);

    const filterParams = {
      ...baseParams,
      module: selectedModuleLabels
    };

    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    switch (page) {
      case 'auditFindings':
        managementOp.fetchMgmtAudits(lang, filterParams).then((data) => {
          dispatch(managementOp.setMgmtAudits(data));
        });
        break;

      case 'category':
        managementOp.fetchCategory(lang, filterParams).then((data) => {
          dispatch(managementOp.setCategory(data));
        });
        break;

      case 'product':
        managementOp.fetchProduct(lang, filterParams).then((data) => {
          dispatch(managementOp.setProduct(data));
        });
        break;

      default:
        return;
    }

    dispatch(managementOp.setDrawTable(true));

    localStorage.setItem('orgContext', JSON.stringify(orgContext));
  };

  useEffect(() => {
    applyFilters();
  }, []);

  const handleSave = () => {
    dispatch(managementOp.setSaveClicked(true));
  };

  useEffect(() => {
    dispatch(managementOp.setFilters(lang));
  }, []);

  const industryArray = Array.isArray(industry)
    ? industry
    : industry
    ? [industry]
    : [];

  /* ---------------- fetch modules ---------------- */
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const industries = Array.isArray(industryArray)
          ? industryArray
          : industryArray
          ? [industryArray]
          : [];

        if (!industries.length) {
          setModuleOptionsState([]);
          return;
        }

        const ids = industries.map((i: any) => i.value ?? i);

        const responses = await Promise.all(
          ids.map((id: number) =>
            ApiService.getModulesByIndustries({
              lang,
              ind_id: id
            })
          )
        );

        const merged = responses.flatMap((r) => r?.data?.data ?? []);

        const unique = Array.from(
          new Map(merged.map((m: any) => [m.mod_id, m])).values()
        );

        setModuleOptionsState(
          unique
            .filter((m: any) => m?.mod_id != null)
            .map((m: any) => ({
              value: m.mod_id,
              label: m.module
            }))
        );
        const modules = unique
          .filter((m: any) => m?.mod_id != null)
          .map((m: any) => ({
            value: m.mod_id,
            label: m.module
          }));

        setModuleOptionsState(modules);
        dispatch(managementOp.setModules(modules));
      } catch (err) {
        console.error(err);
        setModuleOptionsState([]);
      }
    };

    fetchModules();
  }, [JSON.stringify(industryArray), lang]);

  const selectedModuleValues = Array.isArray(orgContext.module)
    ? orgContext.module
    : [];

  useEffect(() => {
    dispatch(complianceEstOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={titles[titleKey]}
        goBackButtons={
          <>
            <GoBackButton
              to={
                page !== 'auditFindings'
                  ? '/data_management_workflow'
                  : '/workingAreaDetails/audit-findings'
              }
              text={t('GO_BACK')}
              checkUnsavedChanges={true}
            />
            <GoBackButton
              to='/working_area'
              text={t('GO_TO_WORKING_AREAS')}
              checkUnsavedChanges={true}
            />
          </>
        }
      />
      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        {!industryArray.length ? (
          <Popover
            content="Please select Industry in 'Who Am I' to enable Modules"
            placement='top'
          >
            <div>
              <CustomSelect disabled mode='multiple' />
            </div>
          </Popover>
        ) : (
          <CustomSelect
            size='md'
            mode='multiple'
            options={moduleOptionsState}
            value={selectedModuleValues}
            onChange={(value: any[]) => {
              dispatch(
                globalOp.setHierarchicalLevelsScope({
                  ...orgContext,
                  module: (value || [])
                    .map((v) => {
                      const option = moduleOptionsState.find(
                        (o) => o.value === (typeof v === 'object' ? v.value : v)
                      );

                      return option
                        ? { value: option.value, label: option.label }
                        : null;
                    })
                    .filter(Boolean)
                })
              );
            }}
          />
        )}
        <Styled.ActionButtons
          isDataManagementPage={true}
          hasDisplayedData={drawTable}
        >
          <Styled.ApplyButton
            type='primary'
            onClick={applyFilters}
            scheme={scheme}
          >
            {t('DISPLAY')}
          </Styled.ApplyButton>
          {drawTable && (
            <>
              <Styled.ApplyButton
                disabled={saveDisabled}
                onClick={handleSave}
                scheme={scheme}
              >
                {t('SAVE')}
              </Styled.ApplyButton>
            </>
          )}
        </Styled.ActionButtons>
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider />
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default DataManagementLayout;
